/* eslint-disable complexity */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import { useLocation, useParams } from 'react-router-dom';

import Box from 'carbon-react/lib/components/box/box.component';
import DateInput, { DateChangeEvent } from 'carbon-react/lib/components/date';
import { MultiSelect, Option, Select } from 'carbon-react/lib/components/select';

import { FinData } from '@futrli/predict/report-builder/types';
import { forceLocalDate } from '@futrli/predict/utils/dates';
import { getPeriodOffset, getPeriodType } from '@futrli/predict/report-builder/utils-dates';
import { getReportBulkLiveData } from '@futrli/predict/core/api/reporting/report-builder';
import { localizeFinData } from '@futrli/predict/report-builder/utils-data';
import { Logger, matchConsolidationsRoutePath } from '@futrli/shared-services';
import { toastError } from '@futrli/ui-components';
import { useIntl } from '@futrli/localisation';
import { V2FinancialDatasetRequest } from '@futrli/predict/report-builder/utils-queries';

import { EmptyForecastContent } from '../empty-forecasts-content';
import { ForecastingLineChart } from '../forecasting-line-chart/forecasting-line-chart';
import { getChartValues, getPeriodCount } from '../../utils/utils';
import {
  normalizePerformanceComparisonMetric,
  PERFORMANCE_COMPARISON_FILTERS_LOCAL_STORAGE_KEY,
  PERFORMANCE_COMPARISON_METRICS,
  PerformanceComparisonMetricId
} from './performance-comparison-metrics';
import { OrgOrConForecastListItem, PerformanceComparisonFiltersData } from '../../types';

import * as styles from './performance-comparison.scss';

interface Props {
  forecasts?: OrgOrConForecastListItem[];
}

const getSelectedForecasts = (
  organisationId: string,
  performanceComparisonFiltersObj: PerformanceComparisonFiltersData | null,
  forecasts?: OrgOrConForecastListItem[]
) => {
  if (!forecasts) {
    return [];
  }

  if (performanceComparisonFiltersObj?.[organisationId]) {
    return forecasts.filter(f =>
      performanceComparisonFiltersObj[organisationId].currentForecastsIds.some(id => id === f.id)
    );
  }

  const liveForecast = forecasts.filter(f => f.kind === 'live');

  if (!liveForecast[0] || liveForecast[0].is_hidden) {
    return [];
  }

  return liveForecast;
};

export const PerformanceComparison = ({ forecasts }: Props) => {
  const intl = useIntl();
  const { workspaceId: tempWorkspaceId, organisationId: tempOrganisationId } = useParams();
  const workspaceId = tempWorkspaceId || '';
  const organisationId = tempOrganisationId || '';
  const location = useLocation();
  const isConsolidation = !!matchConsolidationsRoutePath(location.pathname);
  const performanceComparisonFiltersString = localStorage.getItem(PERFORMANCE_COMPARISON_FILTERS_LOCAL_STORAGE_KEY);

  const comparisonMetrics = useMemo(
    () => PERFORMANCE_COMPARISON_METRICS.map(metric => ({ id: metric.id, name: intl.formatMessage(metric.message) })),
    [intl]
  );

  const performanceComparisonFiltersObj = useMemo(
    () =>
      performanceComparisonFiltersString
        ? (JSON.parse(performanceComparisonFiltersString) as PerformanceComparisonFiltersData)
        : null,
    [performanceComparisonFiltersString]
  );

  const autocompleteOptions = useMemo(() => {
    const options = forecasts?.filter(f => {
      if (f.kind === 'live' && f.is_hidden) {
        return false;
      }
      return true;
    });
    return options || [];
  }, [forecasts]);

  const [selectedForecasts, setSelectedForecasts] = useState<OrgOrConForecastListItem[]>(
    getSelectedForecasts(organisationId, performanceComparisonFiltersObj, forecasts)
  );
  const hasInitializedForecastSelection = useRef(Boolean(forecasts?.length));

  useEffect(() => {
    if (!hasInitializedForecastSelection.current && forecasts?.length) {
      hasInitializedForecastSelection.current = true;
      setSelectedForecasts(getSelectedForecasts(organisationId, performanceComparisonFiltersObj, forecasts));
    }
  }, [forecasts, organisationId, performanceComparisonFiltersObj]);

  const [comparisonFromDate, setComparisonFromDate] = useState(
    performanceComparisonFiltersObj?.[organisationId]
      ? new Date(performanceComparisonFiltersObj[organisationId].fromDate)
      : new Date()
  );
  const [financialData, setFinancialData] = useState<Record<string, FinData>>();
  const [isLoadingFinancialData, setIsLoadingFinancialData] = useState(false);
  const [period, setPeriod] = useState(
    performanceComparisonFiltersObj?.[organisationId]
      ? performanceComparisonFiltersObj[organisationId].periodType
      : 'monthly'
  );
  const [comparisonMetric, setComparisonMetric] = useState<PerformanceComparisonMetricId>(
    normalizePerformanceComparisonMetric(performanceComparisonFiltersObj?.[organisationId]?.comparisonMetric)
  );
  const [initialLoad, setInitialLoad] = useState(true);

  const sendDatasetRequest = useCallback(
    async (
      currentForecasts: OrgOrConForecastListItem[],
      comparisonMetric: PerformanceComparisonMetricId,
      fromDate: string,
      periodType: string
    ) => {
      if (currentForecasts.length) {
        const queries: Record<string, V2FinancialDatasetRequest> = {};
        currentForecasts.forEach(f => {
          queries[f.id] = {
            forecast_id: f.id,
            name: f.name,
            consolidated_forecast_id: f.id,
            period_type: getPeriodType(periodType) || '',
            period_count: getPeriodCount(periodType),
            start_date: fromDate,
            fields: [{ ref: comparisonMetric }],
            facet: null,
            period_offset: getPeriodOffset(periodType),
            extra_columns: [],
            actuals_only: false,
            include_assumptions: false,
            include_zero_rows: true
          };
        });

        try {
          setIsLoadingFinancialData(true);
          const fetchedData = await getReportBulkLiveData(workspaceId, organisationId, queries, isConsolidation);
          const localizedFinData = Object.fromEntries(
            Object.entries(fetchedData).map(([key, value]) => [key, localizeFinData([value], intl)[0]])
          ) as Record<string, FinData>;
          setFinancialData(localizedFinData);

          if (!performanceComparisonFiltersObj) {
            localStorage.setItem(
              PERFORMANCE_COMPARISON_FILTERS_LOCAL_STORAGE_KEY,
              JSON.stringify({
                [organisationId]: {
                  currentForecastsIds: currentForecasts.map(f => f.id),
                  comparisonMetric,
                  fromDate,
                  periodType
                }
              })
            );
          } else {
            localStorage.setItem(
              PERFORMANCE_COMPARISON_FILTERS_LOCAL_STORAGE_KEY,
              JSON.stringify({
                ...performanceComparisonFiltersObj,
                [organisationId]: {
                  currentForecastsIds: currentForecasts.map(f => f.id),
                  comparisonMetric,
                  fromDate,
                  periodType
                }
              })
            );
          }
        } catch (e) {
          Logger.error(e as string);
          toastError();
        } finally {
          setIsLoadingFinancialData(false);
        }
      }
    },
    [isConsolidation, workspaceId, organisationId, performanceComparisonFiltersObj, intl]
  );

  // initially fetch live forecast data
  useEffect(() => {
    if (initialLoad && selectedForecasts && selectedForecasts.length) {
      sendDatasetRequest(selectedForecasts, comparisonMetric, format(comparisonFromDate, 'yyyy-MM-dd'), period);
      setInitialLoad(false);
    }
  }, [initialLoad, selectedForecasts, comparisonMetric, comparisonFromDate, period, sendDatasetRequest]);

  const handleComparisonFromDateChange = (e: DateChangeEvent) => {
    const newDate = forceLocalDate(e.target.value.rawValue || '');
    setComparisonFromDate(newDate);
    sendDatasetRequest(selectedForecasts, comparisonMetric, format(newDate, 'yyyy-MM-dd'), period);
  };

  const handleAutocompleteChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!forecasts) {
      return;
    }

    const newForecastIds = (event.target.value as unknown) as string[];
    const idSet = new Set(newForecastIds);
    const filteredForecasts = forecasts.filter(obj => idSet.has(obj.id));

    setSelectedForecasts(filteredForecasts);
    sendDatasetRequest(filteredForecasts, comparisonMetric, format(comparisonFromDate, 'yyyy-MM-dd'), period);
  };

  const handlePeriodValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPeriod = event.target.value;
    setPeriod(newPeriod);
    sendDatasetRequest(selectedForecasts, comparisonMetric, format(comparisonFromDate, 'yyyy-MM-dd'), newPeriod);
  };

  const handleComparisonMetricChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newComparisonMetric = normalizePerformanceComparisonMetric(event.target.value);
    setComparisonMetric(newComparisonMetric);
    sendDatasetRequest(selectedForecasts, newComparisonMetric, format(comparisonFromDate, 'yyyy-MM-dd'), period);
  };

  let placeholderText = '';

  if (autocompleteOptions.length === 0) {
    placeholderText = intl.formatMessage({
      id: 'predict.please_create_initial_budget_forecast',
      defaultMessage: 'Please create an initial budget or forecast'
    });
  }

  if (autocompleteOptions.length > 0 && selectedForecasts.length === 0) {
    placeholderText = intl.formatMessage({
      id: 'predict.please_choose_budget_forecast',
      defaultMessage: 'Please choose budget or forecast'
    });
  }

  return (
    <div style={{ marginTop: 0 }}>
      <div className={styles.performanceComparisonControlsCarbon}>
        <Box minWidth={'350px'}>
          <MultiSelect
            multiple
            name="Select forecast"
            id="select-forecast"
            label={intl.formatMessage({
              id: 'predict.forecasting.dashboard.dataset_to_compare',
              defaultMessage: 'Dataset to compare'
            })}
            placeholder={placeholderText}
            value={selectedForecasts.map(s => s.id)}
            onChange={handleAutocompleteChange}
            width="350px"
            maxWidth="450px"
          >
            {autocompleteOptions.map((f: OrgOrConForecastListItem) => (
              <Option key={f.id} text={f.name} value={f.id} />
            ))}
          </MultiSelect>
        </Box>

        <Box className={styles.dataPeriodSelect} width="170px">
          <Select
            label={intl.formatMessage({ id: 'predict.comparison_metric', defaultMessage: 'Comparison metric' })}
            value={comparisonMetric}
            onChange={handleComparisonMetricChange}
            name="Comparison metric select"
          >
            {comparisonMetrics.map(metric => (
              <Option key={metric.id} text={metric.name} value={metric.id} />
            ))}
          </Select>
        </Box>

        <DateInput
          label={intl.formatMessage({
            id: 'predict.from_date',
            defaultMessage: 'From date'
          })}
          value={intl.formatDate(comparisonFromDate)}
          onChange={handleComparisonFromDateChange}
        />

        <Box className={styles.dataPeriodSelect} width="160px">
          <Select
            label={intl.formatMessage({
              id: 'predict.forecasting.dashboard.timeframe',
              defaultMessage: 'Timeframe'
            })}
            value={period}
            onChange={handlePeriodValueChange}
            name="Data period select"
          >
            <Option text={intl.formatMessage({ id: 'common.Days', defaultMessage: 'Days' })} value="daily" />
            <Option
              text={intl.formatMessage({ id: 'common.weeks_mon_sun', defaultMessage: 'Weeks (Mon-Sun)' })}
              value="weekly_mon"
            />
            <Option
              text={intl.formatMessage({ id: 'common.weeks_sun_sat', defaultMessage: 'Weeks (Sun-Sat)' })}
              value="weekly_sun"
            />
            <Option text={intl.formatMessage({ id: 'common.Months', defaultMessage: 'Months' })} value="monthly" />
            <Option
              text={intl.formatMessage({ id: 'common.quarters', defaultMessage: 'Quarters' })}
              value="quarterly"
            />
            <Option text={intl.formatMessage({ id: 'common.Years', defaultMessage: 'Years' })} value="yearly" />
          </Select>
        </Box>
      </div>
      <div style={{ marginTop: 20 }}>
        {autocompleteOptions.length === 0 ? (
          <EmptyForecastContent isConsolidation={isConsolidation} />
        ) : (
          <ForecastingLineChart
            comparisonMetricName={comparisonMetrics.find(c => c.id === comparisonMetric)?.name}
            data={
              isLoadingFinancialData
                ? []
                : selectedForecasts.map(f => ({
                    labels: financialData?.[f.id]?.periods || [],
                    datasets: {
                      label: f.name,
                      data: getChartValues(f, comparisonMetric, financialData)
                    }
                  }))
            }
            isLoading={isLoadingFinancialData}
          />
        )}
      </div>
    </div>
  );
};
